import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Megaphone, LogOut, Plus, Pencil, Trash2, Loader2, PlayCircle, StopCircle, Clock,
  Trophy, Target, TrendingUp, Layers, DollarSign, Building2, Phone, MapPin, CalendarClock,
  MessageSquarePlus, MessageCircle, AlertTriangle,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { STATUS_META, LEAD_STATUSES, money, fmtDate, fmtHM, PLAN_OPTIONS, waLink, followUpDue } from "../lib/marketing";

const Kpi = ({ icon: Icon, label, value, tone = "blue", testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold">
      <span className={`grid h-7 w-7 place-items-center rounded-lg bg-${tone}-50 text-${tone}-600`}><Icon className="h-4 w-4" /></span>
      {label}
    </div>
    <div className="mt-2 text-2xl font-extrabold text-slate-900">{value}</div>
  </div>
);

const emptyLead = { company_name: "", contact_name: "", phone: "", city: "", expected_plan: "", monthly_value: "", status: "new", next_follow_up: "", note: "" };

function LeadDialog({ open, onOpenChange, lead, onSaved }) {
  const editing = Boolean(lead?.id);
  const [f, setF] = useState(emptyLead);
  const [busy, setBusy] = useState(false);
  const [noteText, setNoteText] = useState("");

  useEffect(() => {
    if (open) {
      setF(lead?.id ? {
        company_name: lead.company_name || "", contact_name: lead.contact_name || "", phone: lead.phone || "",
        city: lead.city || "", expected_plan: lead.expected_plan || "", monthly_value: lead.monthly_value || "",
        status: lead.status || "new", next_follow_up: (lead.next_follow_up || "").slice(0, 10), note: "",
      } : emptyLead);
      setNoteText("");
    }
  }, [open, lead]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const save = async () => {
    if (!f.company_name.trim()) { toast.warning("اسم الشركة مطلوب"); return; }
    setBusy(true);
    try {
      const payload = {
        company_name: f.company_name, contact_name: f.contact_name, phone: f.phone, city: f.city,
        expected_plan: f.expected_plan, monthly_value: parseFloat(f.monthly_value) || 0,
        status: f.status, next_follow_up: f.next_follow_up || null,
      };
      if (editing) await api.marketerUpdateLead(lead.id, payload);
      else await api.marketerCreateLead({ ...payload, note: f.note });
      toast.success(editing ? "تم التحديث" : "تمت إضافة العميل");
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); }
    finally { setBusy(false); }
  };

  const addNote = async () => {
    if (!noteText.trim() || !editing) return;
    try { await api.marketerAddNote(lead.id, noteText.trim()); setNoteText(""); toast.success("تمت إضافة الملاحظة"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "خطأ"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="font-body sm:max-w-lg" data-testid="lead-dialog">
        <DialogHeader className="text-start">
          <DialogTitle className="font-heading text-xl">{editing ? "تعديل العميل" : "إضافة عميل محتمل"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1"><Label>اسم الشركة *</Label>
            <Input data-testid="lead-company-input" value={f.company_name} onChange={set("company_name")} /></div>
          <div className="space-y-1"><Label>شخص التواصل</Label><Input value={f.contact_name} onChange={set("contact_name")} /></div>
          <div className="space-y-1"><Label>الجوال</Label><Input dir="ltr" value={f.phone} onChange={set("phone")} /></div>
          <div className="space-y-1"><Label>المدينة</Label><Input value={f.city} onChange={set("city")} /></div>
          <div className="space-y-1"><Label>القيمة الشهرية</Label>
            <Input data-testid="lead-value-input" type="number" dir="ltr" value={f.monthly_value} onChange={set("monthly_value")} /></div>
          <div className="space-y-1"><Label>الباقة المتوقعة</Label>
            <Select value={f.expected_plan || undefined} onValueChange={set("expected_plan")}>
              <SelectTrigger data-testid="lead-plan-select"><SelectValue placeholder="اختر" /></SelectTrigger>
              <SelectContent>{PLAN_OPTIONS.map((p) => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label>الحالة</Label>
            <Select value={f.status} onValueChange={set("status")}>
              <SelectTrigger data-testid="lead-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2 space-y-1"><Label className="flex items-center gap-1"><CalendarClock className="h-4 w-4" /> موعد المتابعة القادم</Label>
            <Input type="date" dir="ltr" value={f.next_follow_up} onChange={set("next_follow_up")} /></div>
          {!editing && (
            <div className="col-span-2 space-y-1"><Label>ملاحظة أولى</Label>
              <Textarea rows={2} value={f.note} onChange={set("note")} /></div>
          )}
        </div>

        {editing && (
          <div className="rounded-xl border border-slate-200 p-3 bg-slate-50">
            <div className="flex items-center gap-2">
              <Input data-testid="lead-note-input" placeholder="أضف ملاحظة متابعة…" value={noteText} onChange={(e) => setNoteText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} />
              <Button size="sm" variant="outline" onClick={addNote} data-testid="lead-note-add" className="gap-1 shrink-0"><MessageSquarePlus className="h-4 w-4" /> إضافة</Button>
            </div>
            <div className="mt-2 max-h-32 overflow-auto space-y-1.5">
              {(lead.notes || []).slice().reverse().map((n, i) => (
                <div key={i} className="text-xs text-slate-600 bg-white rounded-lg border border-slate-100 px-2 py-1.5">
                  <span className="text-slate-400 ml-1">{fmtDate(n.at)} ·</span>{n.text}
                </div>
              ))}
              {(!lead.notes || lead.notes.length === 0) && <p className="text-xs text-slate-400">لا ملاحظات بعد.</p>}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button disabled={busy} onClick={save} data-testid="lead-save-btn" className="bg-emerald-600 hover:bg-emerald-700 gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} حفظ
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MarketerPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = async () => { await logout(); navigate("/marketing", { replace: true }); };
  const [data, setData] = useState({ leads: [], stats: null });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [me, setMe] = useState(null);
  const [clockBusy, setClockBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, m] = await Promise.all([api.marketerLeads(), api.marketerMe()]);
      setData(d); setMe(m);
    } catch (e) { /* session handled by App */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleClock = async () => {
    setClockBusy(true);
    try {
      if (me?.clocked_in) { await api.marketerClockOut(); toast.success("تم تسجيل الانصراف"); }
      else { await api.marketerClockIn(); toast.success("تم تسجيل الحضور"); }
      const m = await api.marketerMe(); setMe(m);
    } catch (e) { toast.error("خطأ في تسجيل الدوام"); }
    finally { setClockBusy(false); }
  };

  const quickStatus = async (lead, status) => {
    try { await api.marketerUpdateLead(lead.id, { status }); toast.success("تم تحديث الحالة"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "خطأ"); }
  };

  const doDelete = async () => {
    if (!delTarget) return;
    try { await api.marketerDeleteLead(delTarget.id); toast.success("تم الحذف"); setDelTarget(null); load(); }
    catch (e) { toast.error("تعذّر الحذف"); }
  };

  const s = data.stats || {};
  const leads = filter === "all" ? data.leads : data.leads.filter((l) => l.status === filter);
  const dueLeads = data.leads.filter((l) => ["new", "contacted", "quoted", "negotiation"].includes(l.status) && followUpDue(l.next_follow_up));
  const target = Number(s.monthly_target) || 0;
  const wonMonth = Number(s.won_month) || 0;
  const targetPct = target ? Math.min(100, Math.round((wonMonth / target) * 100)) : 0;
  const targetTone = targetPct >= 100 ? "emerald" : targetPct >= 80 ? "amber" : "blue";

  return (
    <div dir="rtl" className="min-h-screen bg-slate-50 font-body" data-testid="marketer-portal">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto max-w-6xl px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <div className="grid h-9 w-9 place-items-center rounded-lg bg-gradient-to-br from-emerald-500 to-blue-600 text-white"><Megaphone className="h-5 w-5" /></div>
            <div className="leading-none">
              <div className="font-heading font-extrabold text-slate-900">فريق التسويق</div>
              <div className="text-xs text-slate-400 mt-0.5">{user?.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 h-9 text-xs text-slate-600">
              <Clock className="h-4 w-4 text-emerald-600" /> اليوم: {fmtHM(me?.seconds_today)}
            </div>
            <Button size="sm" onClick={toggleClock} disabled={clockBusy} data-testid="marketer-clock-btn"
              className={`gap-1.5 ${me?.clocked_in ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"}`}>
              {clockBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : me?.clocked_in ? <StopCircle className="h-4 w-4" /> : <PlayCircle className="h-4 w-4" />}
              {me?.clocked_in ? "انصراف" : "حضور"}
            </Button>
            <Button size="sm" variant="outline" onClick={doLogout} data-testid="marketer-logout-btn" className="gap-1.5 text-rose-600 border-rose-200"><LogOut className="h-4 w-4" /></Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Kpi testid="kpi-won" icon={Trophy} label="مشتركون" value={s.won ?? 0} tone="emerald" />
          <Kpi testid="kpi-inprogress" icon={Layers} label="تحت الإجراء" value={s.in_progress ?? 0} tone="amber" />
          <Kpi testid="kpi-pipeline" icon={TrendingUp} label="قيمة قيد التفاوض" value={money(s.pipeline_value)} tone="blue" />
          <Kpi testid="kpi-wonvalue" icon={DollarSign} label="قيمة محقّقة/شهر" value={money(s.won_value)} tone="emerald" />
          <Kpi testid="kpi-conversion" icon={Target} label="نسبة التحويل" value={`${s.conversion ?? 0}%`} tone="indigo" />
        </div>

        {target > 0 && (
          <div data-testid="target-progress" className={`rounded-2xl border p-4 bg-white shadow-sm border-${targetTone}-200`}>
            <div className="flex items-center justify-between text-sm">
              <span className="font-bold text-slate-800 flex items-center gap-1.5"><Target className={`h-4 w-4 text-${targetTone}-600`} /> هدف هذا الشهر</span>
              <span className="font-extrabold text-slate-900">{wonMonth} / {target}</span>
            </div>
            <div className="mt-2 h-3 rounded-full bg-slate-100 overflow-hidden">
              <div className={`h-full bg-${targetTone}-500 transition-all`} style={{ width: `${targetPct}%` }} />
            </div>
            <div className={`mt-1.5 text-xs font-semibold text-${targetTone}-700`}>
              {targetPct >= 100 ? "🎉 مبروك! حقّقت هدف الشهر" : targetPct >= 80 ? `أنت قريب جداً — باقٍ ${target - wonMonth} اشتراك فقط!` : `أنجزت ${targetPct}% من هدفك`}
            </div>
          </div>
        )}

        {dueLeads.length > 0 && (
          <div data-testid="due-banner" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-center gap-2 text-amber-800 text-sm font-semibold">
            <AlertTriangle className="h-4 w-4 shrink-0" /> لديك {dueLeads.length} متابعة مستحقّة اليوم أو متأخّرة — تواصل معهم الآن.
          </div>
        )}

        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 flex-wrap">
            <button onClick={() => setFilter("all")} data-testid="filter-all"
              className={`text-xs font-semibold rounded-full px-3 py-1.5 border ${filter === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>الكل ({data.leads.length})</button>
            {LEAD_STATUSES.map((st) => (
              <button key={st} onClick={() => setFilter(st)} data-testid={`filter-${st}`}
                className={`text-xs font-semibold rounded-full px-3 py-1.5 border ${filter === st ? STATUS_META[st].cls + " ring-1" : "bg-white text-slate-600 border-slate-200"}`}>
                {STATUS_META[st].label} ({(s.funnel || {})[st] ?? 0})
              </button>
            ))}
          </div>
          <Button size="sm" onClick={() => { setEditingLead(null); setDialogOpen(true); }} data-testid="add-lead-btn" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"><Plus className="h-4 w-4" /> إضافة عميل</Button>
        </div>

        {loading ? (
          <div className="py-20 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : leads.length === 0 ? (
          <div className="py-16 text-center text-slate-400 border border-dashed border-slate-200 rounded-2xl">لا يوجد عملاء بعد — أضف أول عميل محتمل 🚀</div>
        ) : (
          <div className="grid gap-3">
            {leads.map((l) => (
              <div key={l.id} data-testid={`lead-card-${l.id}`} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-slate-400 shrink-0" />
                      <span className="font-bold text-slate-900 truncate">{l.company_name}</span>
                      <Badge variant="outline" className={STATUS_META[l.status]?.cls}>{STATUS_META[l.status]?.label}</Badge>
                      {["new", "contacted", "quoted", "negotiation"].includes(l.status) && followUpDue(l.next_follow_up) && (
                        <Badge variant="outline" className={followUpDue(l.next_follow_up) === "overdue" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200"}>
                          {followUpDue(l.next_follow_up) === "overdue" ? "متابعة متأخرة" : "متابعة اليوم"}
                        </Badge>
                      )}
                    </div>
                    <div className="mt-1.5 flex items-center gap-3 flex-wrap text-xs text-slate-500">
                      {l.contact_name && <span>{l.contact_name}</span>}
                      {l.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {l.phone}</span>}
                      {l.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {l.city}</span>}
                      {l.monthly_value ? <span className="flex items-center gap-1 text-emerald-700 font-semibold"><DollarSign className="h-3 w-3" /> {money(l.monthly_value)}</span> : null}
                      {l.next_follow_up && <span className="flex items-center gap-1 text-amber-700"><CalendarClock className="h-3 w-3" /> متابعة: {fmtDate(l.next_follow_up)}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Select value={l.status} onValueChange={(v) => quickStatus(l, v)}>
                      <SelectTrigger data-testid={`quick-status-${l.id}`} className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{LEAD_STATUSES.map((st) => <SelectItem key={st} value={st}>{STATUS_META[st].label}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => { setEditingLead(l); setDialogOpen(true); }} data-testid={`edit-lead-${l.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    {l.phone && (
                      <Button size="icon" variant="outline" className="h-8 w-8 text-emerald-600 border-emerald-200"
                        onClick={() => window.open(waLink(l.phone, `مرحباً، بخصوص اشتراك شركة ${l.company_name} في منصة WorkZone`), "_blank")}
                        data-testid={`wa-lead-${l.id}`}><MessageCircle className="h-3.5 w-3.5" /></Button>
                    )}
                    <Button size="icon" variant="outline" className="h-8 w-8 text-rose-600 border-rose-200" onClick={() => setDelTarget(l)} data-testid={`del-lead-${l.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <LeadDialog open={dialogOpen} onOpenChange={setDialogOpen} lead={editingLead} onSaved={load} />

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent dir="rtl" className="font-body">
          <AlertDialogHeader><AlertDialogTitle>حذف العميل؟</AlertDialogTitle>
            <AlertDialogDescription>سيتم حذف «{delTarget?.company_name}» نهائياً.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-del-lead">حذف</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
